/* global om */
import { Fragment, useEffect, useState } from 'react';
import Head from 'next/head';
import key from 'weak-key';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { request } from 'graphql-request';
import { ReactSVG } from 'react-svg';
import dynamic from 'next/dynamic';
import { getStructuredData, getContentPaths, getBlocksFromQueue, getAlias, verifyAnalytics, getDrupalSettings } from '~/utils/utils';
import BlockParser from '~/components/blockParser/blockParser';
import BodyParser from '~/components/bodyParser/bodyParser';
import PageWrapper from '~/components/wrappers/pageWrapper';
import Share from '~/components/share/share';
import SiteSettings from '~/siteSettings';
import ScrollIntersectionTrigger from '~/components/scrollIntersectionTrigger/scrollIntersectionTrigger';
import Skimlinks from '~/components/skimlinks/skimlinks';
import Trackonomics from '~/components/trackonomics/trackonomics';
import TaxLinks from '~/components/taxLinks/taxLinks';

// GQL
import GET_GALLERY from '~/graphql/GET_GALLERY.graphql';

const Utag = dynamic(() => import('~/components/utag/utag'), { ssr: false });
const BidBarrelReact = dynamic(() => import('~/components/bidBarrelReact/bidBarrelReact'), { ssr: false });

/**
 * Gallery
 * @category Page
 * @extends Component
 * @summary Display the gallery page.
 * @return {Node} React node containing the gallery page.
 */

const trackImgView = async (data) => {
  const isAnalyticsVerified = await verifyAnalytics(100, 10);

  if(isAnalyticsVerified) {
    om.trackPhotoView(data);
  }
}

const TrackingWrapper = ({ condition, isTrue, isFalse, children }) => {
  return condition
    ? isTrue(children)
    : isFalse(children);
};

const Gallery = props => {
  const { router, sitePrimaryRsid, baseUrl, fileUrl, data, blocks, drupalSettings } = props;
  const path = router?.query?.alias
    ? `/gallery/${ router.query.alias.join('/') }`
    : '/';

  const content = {
    ...data.nodeQuery
  };

  const keywords = content.field_tags && content.field_tags.map(tag => tag.name).join(',');

  const [trackableImages, setTrackableImages] = useState([1]);
  const enableTrackImage = (imgId) => {
    if(!trackableImages.includes(imgId) && typeof window !== 'undefined') {
      setTrackableImages([...trackableImages, imgId]);
    }
  }

  useEffect(() => {
    // Disable scroll restoration on this page, it causes tracking events to fire when they shouldn't.
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    return () => {
      window.history.scrollRestoration = 'auto';
    };
  });

  return <>
    <Head>
      <title>{ content.title } | { SiteSettings.siteName }</title>
      <meta name='title' content={ content.title } />
      <link rel='canonical' href={`${ baseUrl }${ path }`} />
      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:title' content={ content.field_social_headline || content.title } />
      <meta name='description' content={ content.field_seo_description || content.field_subhead } />
      <meta property='og:url' content={ path } />
      <meta property='og:title' content={ content.field_social_headline || content.title } />
      <meta property='og:image' content={ content?.field_image?.field_image?.social?.url } />
      <meta property='og:description' content={ content.field_social_description || content.field_subhead } />
      <link rel='image_src' href={ content?.field_image?.field_image?.social?.url } />
      <meta name='twitter:url' content={ path } />
      <meta name='twitter:image:width' content={ content?.field_image?.field_image?.social?.width } />
      <meta name='twitter:image:height' content={ content?.field_image?.field_image?.social?.height } />
      <meta name='twitter:image' content={ content?.field_image?.field_image?.social?.url } />
      <meta property='article:published_time' content={ content.field_display_date } />
      <meta name='keywords' content={ keywords || SiteSettings.defaultKeywords } />
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(getStructuredData(baseUrl, path, content)) }}
      />
      <meta name='robots' content={`${content.field_is_promotional ? 'noindex' : 'index'}, max-image-preview:large`} />
    </Head>
    <div className='content'>
      <div className='site-bg'>
        <div className='constrain'>
          <div className='galleries__header'>
            <div className='galleries__header-icon'>
              <ReactSVG src='/img/icons/gallery.svg' />
            </div>
            <h1>{ content.title }</h1>
          </div>
          {/* Main Image */}
          <div className='gallery__header'>
            { content.field_image &&
              <div className='wrap-relative'>
                <div className='gallery__share'>
                  <Share
                    mediaUrl={ content.field_image.field_image.lg.url }
                    title={ content.title }
                  />
                </div>
                <ScrollIntersectionTrigger action={() => {
                  trackImgView({
                    galleryName: content.title,
                    galleryNavigationType: 'vertical',
                    gallerySize: (content?.field_slides?.length || 0) + 1,
                    galleryType: 'vertical',
                    photoName: content.title,
                    photoPosition: 1,
                    photoType: 'flat',
                    activatePanosphere: false
                  });
                }}>
                  <picture>
                    <source media={ `(max-width: ${ SiteSettings.breakpoints.sm })` } srcSet={ content.field_image.field_image.sm.url } />
                    <img
                      src={ content.field_image.field_image.lg.url }
                      alt={ content.field_image?.alt }
                      width={ content.field_image.field_image.lg.width }
                      height={ content.field_image.field_image.lg.height }
                      className='img-full'
                    />
                  </picture>
                </ScrollIntersectionTrigger>
              </div>
            }
            { !content.field_image &&
              <div className='img-missing'>No Image Available</div>
            }
            <div className='gallery__header-body'>
              <div className='gallery__count'>
                1 / { (content?.field_slides?.length || 0) + 1 }
              </div>
              <h2>
                { content.title }
              </h2>
              {/* Skimlinks */}
              { content.field_skimlinks &&
                <Skimlinks
                  type={ content.__typename }
                  data={ drupalSettings }
                />
              }
              {/* Trackonomics */}
              { content.field_trackonomics &&
                <Trackonomics />
              }
              {/* Body */}
              { content.body &&
                <BodyParser
                  baseDomain={ fileUrl }
                  content={ content.body }
                  drupalSettings={ drupalSettings }
                />
              }
              { content.field_image &&
                <>
                  <hr />
                  <div className='gallery__credit'>
                    { content.field_image.field_credit }
                  </div>
                </>
              }
            </div>
          </div>
          <div data-ad-ref='leader-plus-flex-top' className='bidbarrel-ad'></div>
          <div data-ad-ref='mobile-plus-flex-top' className='bidbarrel-ad'></div>
          {/* Slides */}
          { content?.field_slides?.map?.((slide, i) => (
            <Fragment key={`slide-${key(slide)}`}>
              <div className='gallery__slide'>
                <div className='gallery__slide-body'>
                  <a name={`${ slide.title.replace(' ', '_') }`} className='visuallyhidden'>
                    { slide.title }
                  </a>
                  <div className='gallery__count'>
                    { i + 2 } / { content.field_slides.length + 1 }
                  </div>
                  <h2>{ slide.title }</h2>
                  <div dangerouslySetInnerHTML={{ __html: slide?.field_caption?.replace?.(', full_html', '') }}></div>
                  { content.field_image &&
                    <div className='gallery__body-credit'>
                      <hr />
                      <div className='gallery__credit'>
                        { content.field_image.field_credit }
                      </div>
                    </div>
                  }
                </div>
                <div className='gallery__slide-img'>
                  { slide.field_image_type === 'image' &&
                    <>
                      <div className='gallery__share'>
                        <Share
                          mediaUrl={ slide.field_image.field_image.lg.url }
                          url={ `${ process.env.NEXT_PUBLIC_BASE_URL }${content.path}#${ slide.title.replace(' ', '_') }` }
                          title={ content.title }
                        />
                      </div>
                      <TrackingWrapper
                        condition={ trackableImages.includes(i + 2) }
                        isTrue={children =>
                          <ScrollIntersectionTrigger action={() => {
                            trackImgView({
                              galleryName: content.title,
                              galleryNavigationType: 'vertical',
                              gallerySize: content.field_slides.length + 1,
                              galleryType: 'native',
                              photoName: slide.title,
                              photoPosition: i + 2,
                              photoType: 'flat',
                              activatePanosphere: false
                            });
                          }}>
                            {children}
                          </ScrollIntersectionTrigger>
                        }
                        isFalse={children =>
                          <div>
                            {children}
                          </div>
                        }
                      >
                        <LazyLoadImage
                          effect='blur'
                          alt={ slide.field_image?.alt }
                          src={ slide.field_image.field_image.lg.url }
                          className='img-full'
                          afterLoad={() => enableTrackImage(i + 2)}
                        />
                      </TrackingWrapper>
                    </>
                  }
                </div>
              </div>
              { i === 0 &&
                <>
                  <div data-ad-ref='leader-plus-flex-middle' className='bidbarrel-ad'></div>
                  <div data-ad-ref='mobile-plus-flex-middle' className='bidbarrel-ad'></div>
                </>
              }
              { i !== 0 &&
                <>
                  <div
                    data-ad-ref='leader-plus-flex-inc'
                    data-incremental={ i }
                    className='bidbarrel-ad'
                  ></div>
                  <div
                    data-ad-ref='mobile-plus-flex-inc'
                    data-incremental={ i }
                    className='bidbarrel-ad'
                  ></div>
                </>
              }
            </Fragment>
          ))}
          <TaxLinks content={ content } />
        </div>
      </div>
    </div>
    { blocks?.entitySubQueueQuery?.items &&
      <BlockParser
        blockItems={ blocks.entitySubQueueQuery.items }
      />
    }
    <div data-ad-ref='leader-plus-flex-bottom' className='bidbarrel-ad'></div>
    <div data-ad-ref='mobile-plus-flex-bottom' className='bidbarrel-ad'></div>
    <Utag
      pageType='gallery'
      siteSection={ content?.field_category?.name }
      data={{
        sitePrimaryRsid,
        assetType: 'gallery',
        galleryName: content.title,
        galleryId: content.nid,
        ...(keywords && {keywords})
      }}
    />
    <BidBarrelReact
      data={ content }
      mapToAdUnits={{
        mobile: [
          'mobile-omni-skybox-plus-sticky',
          'mobile-plus-flex-top',
          'mobile-plus-flex-middle',
          'mobile-plus-flex-inc',
          'mobile-plus-flex-bottom'
        ],
        desktop: [
          'omni-skybox-plus-sticky',
          'leader-plus-flex-top',
          'leader-plus-flex-middle',
          'leader-plus-flex-inc',
          'leader-plus-flex-bottom'
        ]
      }}
    />
  </>;
}

export async function getStaticPaths() {
  const paths = await getContentPaths('/gallery/', {
    machine_name: 'latest_content_by_type',
    display: 'master',
    itemsPerPage: parseInt(process.env.PRERENDER_COUNT, 10) || 10,
    offset: 0,
    viewArgs: [
      'gallery'
    ]
  });

  return {
    paths,
    fallback: 'blocking'
  };
}

export async function getStaticProps(ctx) {
  const aliasData = await getAlias({
    alias: `/gallery/${ ctx.params.alias.join('/') }`
  });

  if(!aliasData?.pathAliasQuery?.nid) {
    return {
      notFound: true,
      revalidate: 1
    }
  }

  // Get Data
  const data = await request(process.env.NEXT_PUBLIC_API_HOST || 'https://api-stage.thedrewbarrymoreshow.com/cmv_graphql/api/query', GET_GALLERY, {
    target_id: parseInt(aliasData.pathAliasQuery.nid, 10)
  });

  if(!data?.nodeQuery) {
    return {
      notFound: true,
      revalidate: 1
    }
  }

  // Get Blocks
  const blocks = await getBlocksFromQueue({
    target_id: 'gallery',
    limit: 100
  });

  // Get Site Settings
  const drupalSettings = await getDrupalSettings();

  return {
    props: {
      data,
      blocks,
      drupalSettings
    },
    revalidate: 1
  }
}

export default PageWrapper(Gallery);
